import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import CabinaTopbar from '@/components/cabina/CabinaTopbar'
import { api, ApiException } from '@/lib/api'
import { getAccountQuota, type AccountQuota } from '@/services/listingsService'
import type { PublicSubscriptionPlan } from '@/types'
import { DEMO_QUOTA } from '@/pages/dashboardDemo'

/**
 * Gestión de plan y cupos — identidad visual "Cabina de inteligencia" (Fase 3).
 * Consume `/api/v1/billing/plans` (autenticado, DEC-010) para la lista canónica
 * desde BBDD y `/api/v1/account/quota` para el plan vigente + consumo. La lógica
 * de carga real se conserva; los precios NUNCA se inventan (vienen del backend).
 *
 * El endpoint público devuelve SÓLO la forma {@link PublicSubscriptionPlan}
 * (8 campos). NO accedemos a `single_job_max_leads` ni `carry_over_leads`:
 * no viajan al frontend y leerlos crasheaba la pantalla (gris). Render
 * defensivo en precio/cantidades por si PostgREST manda numéricos como string.
 */

const nInt = (n: number | string | null | undefined) => {
  const v = typeof n === 'string' ? Number(n) : n ?? 0
  return Number.isFinite(v) ? (v as number).toLocaleString('es-ES') : '—'
}

const isPreview =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('preview')

/* Demo plans para el bucle de fidelidad (sin backend). Sólo DEV + ?preview.
 * MISMO shape que el endpoint público (8 campos) — así el preview NO oculta
 * un crash por shape divergente. */
const DEMO_PLANS: PublicSubscriptionPlan[] = [
  {
    code: 'free', name_es: 'Gratuito', name_en: 'Free', monthly_price_eur: 0,
    monthly_leads_quota: 15,
    features: { export_csv: true, export_excel: false, presets_sectoriales: false },
    description_es: 'Para probar MapeoData y mapear tu primera zona.', description_en: '',
  },
  {
    code: 'starter', name_es: 'Starter', name_en: 'Starter', monthly_price_eur: 9.95,
    monthly_leads_quota: 200,
    features: { export_csv: true, export_excel: true, presets_sectoriales: false },
    description_es: 'Para profesionales que trabajan varias zonas al mes.', description_en: '',
  },
  {
    code: 'pro', name_es: 'Pro', name_en: 'Pro', monthly_price_eur: 25.95,
    monthly_leads_quota: 800,
    features: { export_csv: true, export_excel: true, presets_sectoriales: true },
    description_es: 'Para equipos que trabajan varias zonas cada semana.', description_en: '',
  },
]

const PLAN_ICON: Record<string, JSX.Element> = {
  free: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l2.4 7.4H22l-6 4.5 2.3 7.1L12 16.8 5.7 21l2.3-7.1-6-4.5h7.6z" />
    </svg>
  ),
  starter: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M13 2L3 14h7l-1 8 10-12h-7z" />
    </svg>
  ),
  pro: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2a7 7 0 00-4 12.7V17h8v-2.3A7 7 0 0012 2z" /><path d="M9 21h6M10 17v4M14 17v4" />
    </svg>
  ),
}

const Check = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5 9-10" /></svg>
)
const Cross = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 6l12 12M18 6L6 18" /></svg>
)

function Donut({ consumed, quota }: { consumed: number; quota: number }) {
  const r = 50
  const c = 2 * Math.PI * r
  const ratio = quota > 0 ? Math.min(1, consumed / quota) : 0
  const off = c * (1 - ratio)
  return (
    <div className="donut">
      <svg width="116" height="116" viewBox="0 0 116 116">
        <circle cx="58" cy="58" r={r} fill="none" stroke="var(--panel-2)" strokeWidth="12" />
        <circle
          cx="58" cy="58" r={r} fill="none" stroke="var(--accent-2)" strokeWidth="12"
          strokeLinecap="round" strokeDasharray={c.toFixed(2)} strokeDashoffset={off.toFixed(2)}
        />
      </svg>
      <div className="center">
        <div>
          <div className="big">{nInt(consumed)}</div>
          <div className="small">de {nInt(quota)}</div>
        </div>
      </div>
    </div>
  )
}

function daysUntil(iso: string): number {
  const diff = new Date(iso).getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)))
}

interface PlanState {
  loading: boolean
  error: string | null
  plans: PublicSubscriptionPlan[]
  quota: AccountQuota | null
}

export default function Plan() {
  const [state, setState] = useState<PlanState>({
    loading: true, error: null, plans: [], quota: null,
  })

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [resp, quota] = await Promise.all([
          api<{ plans: PublicSubscriptionPlan[] }>('/billing/plans'),
          getAccountQuota().catch(() => null),
        ])
        if (cancelled) return
        setState({ loading: false, error: null, plans: resp.plans ?? [], quota })
      } catch (e: unknown) {
        if (cancelled) return
        if (isPreview) {
          setState({ loading: false, error: null, plans: DEMO_PLANS, quota: DEMO_QUOTA })
          return
        }
        const msg = e instanceof ApiException
          ? `${e.status} ${e.apiError.error}`
          : e instanceof Error ? e.message : 'Error desconocido'
        setState({ loading: false, error: msg, plans: [], quota: null })
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const { loading, error, plans, quota } = state
  const ordered = [...plans].sort((a, b) => Number(a.monthly_price_eur) - Number(b.monthly_price_eur))
  const currentPlan = quota ? ordered.find((p) => p.code === quota.plan_code) : undefined

  return (
    <div className="cabina-root">
      <CabinaTopbar />
      <div className="wrap">
        <div className="page-head">
          <div>
            <div className="crumb">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2a7 7 0 00-4 12.7V17h8v-2.3A7 7 0 0012 2z" /><path d="M9 21h6" />
              </svg>
              Plan y cupos
            </div>
            <h1>Tu plan</h1>
            <p>Gestiona tu suscripción y consulta el cupo de leads que te queda este ciclo.</p>
          </div>
          <Link to="/app/busqueda" className="btn btn-accent">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
            Nueva extracción
          </Link>
        </div>

        {error && (
          <div className="card" style={{ borderLeft: '3px solid var(--danger)' }}>
            <div style={{ color: 'var(--danger)', fontWeight: 600, marginBottom: 4 }}>
              No se pudieron cargar los planes: {error}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => window.location.reload()}>Reintentar</button>
          </div>
        )}

        {loading && (
          <div className="cockpit">
            <div className="card col-12" style={{ minHeight: 120 }}>
              <div className="skeleton-line" style={{ height: 12, width: '30%', marginBottom: 16 }} />
              <div className="skeleton-line" style={{ height: 8, width: '70%' }} />
            </div>
            {[4, 4, 4].map((s, i) => (
              <div key={i} className={`card col-${s}`} style={{ minHeight: 240 }}>
                <div className="skeleton-line" style={{ height: 12, width: '40%', marginBottom: 14 }} />
                <div className="skeleton-line" style={{ height: 8, width: '80%', marginBottom: 8 }} />
                <div className="skeleton-line" style={{ height: 8, width: '60%' }} />
              </div>
            ))}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Cupo del ciclo actual */}
            {quota && currentPlan && (
              <div className="card col-12 accent-edge" style={{ marginBottom: 22 }}>
                <div className="card-head">
                  <span className="ic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                  </span>
                  <div>
                    <h2>Cupo de este ciclo</h2>
                    <div className="sub">Plan {currentPlan.name_es} · renueva en {daysUntil(quota.period_end_at)} días</div>
                  </div>
                </div>
                <div className="quota-panel">
                  <Donut consumed={quota.monthly_leads_consumed} quota={quota.monthly_leads_quota} />
                  <div className="qp-bars">
                    <QuotaBar label="Leads restantes este mes" value={quota.monthly_leads_remaining} total={quota.monthly_leads_quota} />
                    <QuotaBar label="Consumidos este ciclo" value={quota.monthly_leads_consumed} total={quota.monthly_leads_quota} />
                    {quota.daily_leads_remaining !== null && (
                      <QuotaBar label="Restantes hoy" value={quota.daily_leads_remaining} total={quota.daily_leads_consumed + quota.daily_leads_remaining} />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tarjetas de plan */}
            <div className="plan-grid">
              {ordered.map((plan) => {
                const isCurrent = quota?.plan_code === plan.code
                const price = Number(plan.monthly_price_eur)
                const isFree = !Number.isFinite(price) || price === 0
                const features = plan.features ?? { export_csv: false, export_excel: false, presets_sectoriales: false }
                return (
                  <div className={`plan-card${isCurrent ? ' current' : ''}`} key={plan.code}>
                    {isCurrent && (
                      <div className="pc-ribbon">
                        <span className="badge live">Tu plan</span>
                      </div>
                    )}
                    <div className="pc-name">
                      <span className="ic">{PLAN_ICON[plan.code] ?? PLAN_ICON.free}</span>
                      <h2>{plan.name_es}</h2>
                    </div>
                    <div className="pc-price">
                      {isFree ? (
                        <span className="free">Gratis</span>
                      ) : (
                        <>
                          <span className="amt">{price.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</span>
                          <span className="per">/ mes</span>
                        </>
                      )}
                    </div>
                    {plan.description_es && <div className="pc-desc">{plan.description_es}</div>}

                    {/* Sólo campos del shape PÚBLICO (`PublicSubscriptionPlan`).
                        single_job_max_leads / carry_over_leads NO viajan al
                        frontend — leerlos crasheaba la pantalla. */}
                    <div className="plan-feats">
                      <div className="pf">
                        <span className="pf-ic on">{Check}</span>
                        <span><b>{nInt(plan.monthly_leads_quota)}</b> leads al mes</span>
                      </div>
                      <Feat on={features.export_csv} label="Exportar a CSV" />
                      <Feat on={features.export_excel} label="Exportar a Excel" />
                      <Feat on={features.presets_sectoriales} label="Presets sectoriales" />
                    </div>

                    <div className="pc-foot">
                      {isCurrent ? (
                        <span className="plan-current-tag">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12l5 5 9-10" /></svg>
                          Plan activo
                        </span>
                      ) : (
                        <button type="button" className="btn btn-ghost btn-sm pc-cta" disabled title="Disponible próximamente">
                          Cambiar de plan
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <p className="footnote">
              Los precios y cupos se sirven desde tu cuenta. El cambio de plan estará disponible próximamente.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

function Feat({ on, label }: { on: boolean; label: string }) {
  return (
    <div className={`pf${on ? '' : ' muted'}`}>
      <span className={`pf-ic ${on ? 'on' : 'off'}`}>{on ? Check : Cross}</span>
      <span>{label}</span>
    </div>
  )
}

function QuotaBar({ label, value, total }: { label: string; value: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0
  return (
    <div className="q-row">
      <div className="q-top">
        <span className="q-lbl">{label}</span>
        <span className="q-val">{nInt(value)}</span>
      </div>
      <div className="track"><i style={{ width: `${pct}%` }} /></div>
    </div>
  )
}
